import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { EASE, MaskedLines } from "@/components/motion/Reveal";
import { formatPKR, fromPrice, pad2 } from "@/lib/format";
import { cn } from "@/lib/utils";

const DEFAULT_LINES = ["Think", "Adventure.", "Drive Jetour."];

export const Hero = ({ settings = {}, vehicles = [] }) => {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const ref = useRef(null);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 50, damping: 18 });
  const sy = useSpring(my, { stiffness: 50, damping: 18 });
  const rotY = useTransform(sx, [-1, 1], [-7, 7]);
  const rotX = useTransform(sy, [-1, 1], [4, -4]);
  const tx = useTransform(sx, [-1, 1], [-24, 24]);

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const carY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -90]);
  const bgX = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  useEffect(() => {
    if (paused || vehicles.length < 2) return;
    const t = setInterval(() => setActive((a) => (a + 1) % vehicles.length), 6000);
    return () => clearInterval(t);
  }, [paused, vehicles.length]);

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set(((e.clientX - r.left) / r.width - 0.5) * 2);
    my.set(((e.clientY - r.top) / r.height - 0.5) * 2);
  };

  const car = vehicles[active] || null;
  const price = car ? fromPrice(car) : null;
  const lines = settings.hero_lines?.length ? settings.hero_lines : DEFAULT_LINES;

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      onMouseMove={onMove}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => { setPaused(false); mx.set(0); my.set(0); }}
      className="relative min-h-[100svh] overflow-hidden pt-[72px]"
    >
      <div className="grid-texture absolute inset-0 [mask-image:radial-gradient(ellipse_at_center,black_20%,transparent_75%)]" />
      <motion.p style={{ x: bgX }} className="outline-text pointer-events-none absolute left-[-2vw] top-[14%] select-none font-display text-[28vw] font-black leading-none tracking-[-0.06em] lg:top-[8%] lg:text-[24vw]">
        JETOUR
      </motion.p>

      <div className="wrap relative grid min-h-[calc(100svh-72px)] gap-10 pb-40 pt-10 lg:grid-cols-12 lg:items-center lg:pb-44">
        <motion.div style={{ y: textY, opacity: fade }} className="relative z-10 min-w-0 lg:col-span-5">
          {settings.announcement && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.8, ease: EASE }} data-testid="hero-announcement" className="mb-8 flex max-w-full items-center gap-3 overflow-hidden rounded-full border border-white/10 bg-white/5 px-4 py-2 backdrop-blur sm:inline-flex">
              <span className="h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-crimson" />
              <span className="truncate font-mono text-[11px] uppercase tracking-[0.16em] text-slate-300">{settings.announcement}</span>
            </motion.div>
          )}
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5, duration: 0.8 }} className="eyebrow flex items-center gap-4">
            <motion.span className="block h-px w-10 origin-left bg-crimson" initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ delay: 0.6, duration: 0.9, ease: EASE }} />
            <span data-testid="hero-eyebrow">{settings.hero_eyebrow}</span>
          </motion.p>
          <MaskedLines
            lines={lines}
            className="display-xl mt-7"
            lineClassName={(i) => (i === 1 ? "font-editorial italic font-normal normal-case tracking-normal text-gold" : "")}
            delay={0.7}
          />
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2, duration: 0.9, ease: EASE }} data-testid="hero-subtitle" className="mt-8 max-w-md text-base leading-relaxed text-slate-400 sm:text-lg">
            {settings.hero_sub}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.35, duration: 0.9, ease: EASE }} className="mt-10 flex flex-wrap gap-4">
            <Link to="/models" data-testid="hero-explore-button" className="btn-crimson">Explore Models <ArrowUpRight className="h-4 w-4" /></Link>
            <Link to="/test-drive" data-testid="hero-test-drive-button" className="btn-ghost">Book a Test Drive</Link>
          </motion.div>
        </motion.div>

        <div className="relative z-10 min-w-0 lg:col-span-7" style={{ perspective: 1400 }}>
          <div className="spotlight absolute inset-[-25%]" />
          {car && (
            <>
              <AnimatePresence mode="wait">
                <motion.div key={`tag-${car.slug}`} initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }} transition={{ duration: 0.6, ease: EASE }} className="absolute right-0 top-0 z-20 hidden text-right lg:block">
                  <p className="eyebrow">{car.category}</p>
                  <p className="mt-1 font-display text-2xl font-bold tracking-tight" data-testid="hero-active-model">{car.name.replace("JETOUR ", "")}</p>
                  <p className="mt-1 font-mono text-sm text-gold" data-testid="hero-active-price">{price ? `From ${formatPKR(price)}` : "Booking open"}</p>
                </motion.div>
              </AnimatePresence>
              <motion.div style={{ y: carY, rotateY: rotY, rotateX: rotX, x: tx }} className="relative mt-10 lg:mt-16">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={car.slug}
                    src={car.hero_image}
                    alt={car.name}
                    data-testid="hero-car-image"
                    initial={{ x: 180, opacity: 0, filter: "blur(16px)" }}
                    animate={{ x: 0, opacity: 1, filter: "blur(0px)" }}
                    exit={{ x: -180, opacity: 0, filter: "blur(12px)" }}
                    transition={{ duration: 1.05, ease: EASE }}
                    className="relative z-10 w-full object-contain drop-shadow-[0_40px_60px_rgba(0,0,0,0.75)]"
                  />
                </AnimatePresence>
                <img aria-hidden alt="" src={car.hero_image} className="pointer-events-none absolute left-0 top-full -mt-3 w-full -scale-y-100 opacity-[0.1] [mask-image:linear-gradient(to_bottom,transparent_45%,black)]" />
                <div className="absolute -bottom-4 left-1/2 h-12 w-4/5 -translate-x-1/2 rounded-[100%] bg-black/80 blur-2xl" />
              </motion.div>
              <Link to={`/models/${car.slug}`} data-testid="hero-view-model-link" className="group mt-8 inline-flex items-center gap-3 font-display text-sm font-semibold uppercase tracking-[0.12em] text-white lg:absolute lg:bottom-0 lg:right-0 lg:mt-0">
                View {car.name.replace("JETOUR ", "")}
                <span className="flex h-9 w-9 items-center justify-center rounded-full border border-white/20 transition-[background-color,border-color] duration-300 group-hover:border-crimson group-hover:bg-crimson"><ArrowUpRight className="h-4 w-4" /></span>
              </Link>
            </>
          )}
        </div>
      </div>

      <div className="absolute bottom-32 left-5 hidden items-center gap-3 sm:left-8 lg:left-12 lg:flex">
        <span className="relative block h-14 w-px overflow-hidden bg-white/10"><span className="scroll-line absolute inset-0 bg-crimson" /></span>
        <span className="eyebrow">Scroll</span>
      </div>

      {vehicles.length > 0 && (
        <div data-testid="hero-model-switcher" className="absolute inset-x-0 bottom-0 z-20 border-t border-white/10 bg-obsidian/60 backdrop-blur-xl">
          <div className="wrap grid grid-cols-3 divide-x divide-white/10 sm:grid-cols-6">
            {vehicles.map((v, i) => (
              <button
                key={v.slug}
                onClick={() => setActive(i)}
                data-testid={`hero-switch-${v.slug}`}
                className={cn("relative px-3 py-4 text-left transition-colors duration-300 sm:py-5", i === active ? "text-white" : "text-slate-500 hover:text-slate-300")}
              >
                <span className="font-mono text-[10px]">{pad2(i + 1)}</span>
                <span className="mt-1 block truncate font-display text-xs font-semibold uppercase tracking-[0.08em] sm:text-sm">{v.name.replace("JETOUR ", "")}</span>
                {i === active && <motion.span layoutId="hero-active-bar" className="absolute inset-x-0 top-0 h-0.5 bg-crimson" />}
                {i === active && !paused && <motion.span key={`p-${v.slug}`} className="absolute bottom-0 left-0 h-px bg-white/40" initial={{ width: 0 }} animate={{ width: "100%" }} transition={{ duration: 6, ease: "linear" }} />}
              </button>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};
